import client from '@/lib/axios-client'
import type {
	DirecionadaAllListOutput,
	DirecionadaAllListParams,
	DirecionadaInput,
	DirecionadaListOutput,
	DirecionadaListParams,
	DirecionadaMeListOutput,
	DirecionadaMeListParams,
	DirecionadaOutput,
	DirecionadaUpdateInput,
} from '@/types/direcionada'
import {
	assertUploadSizeWithinLimit,
	DEFAULT_MAX_SIZE_MB,
	optimizeImageForUpload,
	sendWithCompressionRetry,
} from '@/utils/image-compression'

const DIRECIONADA_URL = '/direcionadas'

function answersHaveImage(answers?: DirecionadaInput['answers']): boolean {
	return Boolean(answers?.some((answer) => answer.image instanceof File))
}

export async function createDirecionada(
	data: DirecionadaInput,
): Promise<DirecionadaOutput> {
	const send = async (maxSizeMB: number) => {
		const formData = await buildDirecionadaFormData(data, maxSizeMB)
		const response = await client.post(DIRECIONADA_URL, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		})
		return response.data.data
	}

	// Sem imagem, recomprimir nao resolve um 413 — envia sem retry
	return answersHaveImage(data.answers)
		? sendWithCompressionRetry(send)
		: send(DEFAULT_MAX_SIZE_MB)
}

export async function getDirecionadaList(
	params?: DirecionadaListParams,
): Promise<DirecionadaListOutput> {
	const response = await client.get(DIRECIONADA_URL, { params })
	return response.data.data
}

export async function getDirecionadaMeList(
	params?: DirecionadaMeListParams,
): Promise<DirecionadaMeListOutput> {
	const response = await client.get(`${DIRECIONADA_URL}/me`, { params })
	return response.data.data
}

export async function getDirecionadaAllList(
	params?: DirecionadaAllListParams,
): Promise<DirecionadaAllListOutput> {
	const response = await client.get(`${DIRECIONADA_URL}/all`, { params })
	return response.data.data
}

export async function getDirecionadaById(
	id: string,
): Promise<DirecionadaOutput> {
	const response = await client.get(`${DIRECIONADA_URL}/${id}`)
	return response.data.data
}

export async function updateDirecionada(
	id: string,
	data: DirecionadaUpdateInput,
): Promise<DirecionadaOutput> {
	const send = async (maxSizeMB: number) => {
		const formData = await buildDirecionadaUpdateFormData(data, maxSizeMB)
		const response = await client.patch(`${DIRECIONADA_URL}/${id}`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		})
		return response.data.data
	}

	return answersHaveImage(data.answers)
		? sendWithCompressionRetry(send)
		: send(DEFAULT_MAX_SIZE_MB)
}

export async function deleteDirecionada(id: string): Promise<void> {
	await client.delete(`${DIRECIONADA_URL}/${id}`)
}

async function mapAnswersWithImages(
	answers: DirecionadaInput['answers'],
	maxSizeMB: number,
) {
	const answerImages: File[] = []
	const imageCount = answers.filter(
		(answer) => answer.image instanceof File,
	).length
	// Todas as fotos da submissao vao numa unica requisicao multipart, entao o
	// orcamento da requisicao e dividido entre elas — com muitas fotos a qualidade
	// cai, inevitavel com o limite de ~1MB do proxy de producao.
	const maxSizeMBPerImage = imageCount > 0 ? maxSizeMB / imageCount : undefined

	const answersPayload: {
		quizQuestionId: string
		answer?: DirecionadaInput['answers'][number]['answer']
		textAnswer?: string
		responsibleId?: string
		dueDate?: string
		actionPlan?: string
		status?: DirecionadaInput['answers'][number]['status']
		imageFileIndex?: number
		removeImage?: boolean
	}[] = []

	for (const answer of answers) {
		const answerPayload: {
			quizQuestionId: string
			answer?: DirecionadaInput['answers'][number]['answer']
			textAnswer?: string
			responsibleId?: string
			dueDate?: string
			actionPlan?: string
			status?: DirecionadaInput['answers'][number]['status']
			imageFileIndex?: number
			removeImage?: boolean
		} = {
			quizQuestionId: answer.quizQuestionId,
			...(answer.answer ? { answer: answer.answer } : {}),
			...(answer.textAnswer !== undefined
				? { textAnswer: answer.textAnswer }
				: {}),
		}

		if (answer.responsibleId) {
			answerPayload.responsibleId = answer.responsibleId
		}

		if (answer.dueDate) {
			answerPayload.dueDate = answer.dueDate
		}

		if (answer.actionPlan) {
			answerPayload.actionPlan = answer.actionPlan
		}

		if (answer.status) {
			answerPayload.status = answer.status
		}

		if (answer.image instanceof File) {
			const optimizedImage = await optimizeImageForUpload(
				answer.image,
				maxSizeMBPerImage,
			)
			answerImages.push(optimizedImage)
			answerPayload.imageFileIndex = answerImages.length - 1
		}

		if (answer.removeImage) {
			answerPayload.removeImage = true
		}

		answersPayload.push(answerPayload)
	}

	return { answersPayload, answerImages }
}

async function buildDirecionadaFormData(
	data: DirecionadaInput,
	maxSizeMB: number,
): Promise<FormData> {
	const formData = new FormData()
	const { answersPayload, answerImages } = await mapAnswersWithImages(
		data.answers,
		maxSizeMB,
	)
	assertUploadSizeWithinLimit(answerImages)

	formData.append('crId', data.crId)
	if (data.customerId) {
		formData.append('customerId', data.customerId)
	}
	formData.append('quizId', data.quizId)
	formData.append('date', data.date)
	if (data.comment) {
		formData.append('comment', data.comment)
	}
	formData.append('answers', JSON.stringify(answersPayload))

	answerImages.forEach((image) => {
		formData.append('answerImages', image, image.name)
	})

	return formData
}

async function buildDirecionadaUpdateFormData(
	data: DirecionadaUpdateInput,
	maxSizeMB: number,
): Promise<FormData> {
	const formData = new FormData()

	if (data.crId) {
		formData.append('crId', data.crId)
	}

	if (data.customerId) {
		formData.append('customerId', data.customerId)
	}

	if (data.quizId) {
		formData.append('quizId', data.quizId)
	}

	if (data.date) {
		formData.append('date', data.date)
	}

	if (data.comment) {
		formData.append('comment', data.comment)
	}

	if (data.answers?.length) {
		const { answersPayload, answerImages } = await mapAnswersWithImages(
			data.answers,
			maxSizeMB,
		)
		assertUploadSizeWithinLimit(answerImages)
		formData.append('answers', JSON.stringify(answersPayload))

		answerImages.forEach((image) => {
			formData.append('answerImages', image, image.name)
		})
	}

	return formData
}
